import { forwardRef } from "react";
import { useTheme } from "styled-components";
import { IconBox } from "../IconBox/IconBox.js";
import { CheckLinear } from "../Iconsax/CheckLinear.js";
import { CloseLinear } from "../Iconsax/CloseLinear.js";
import { DangerLinear } from "../Iconsax/DangerLinear.js";
import { ICONSAX_SIZE } from "../Iconsax/Iconsax.types.js";
import { InfoCircleBold } from "../Iconsax/InfoCircleBold.js";
import { Text } from "../Text/Text.js";
import { TextSize, TextWeight } from "../Text/Text.types.js";
import { ITheme } from "../Theme/Theme.types.js";
import { TooltipOnTextOverflow } from "../TooltipOnTextOverflow/TooltipOnTextOverflow.js";
import { useTranslationsContext } from "../TranslationsProvider/TranslationsProvider.context.js";
import { Body, Border, CloseIcon, Container, Title } from "./Toast.style.js";
import { DefaultToastProps, ToastProps } from "./Toast.types.js";

const defaultProps: DefaultToastProps = {
    transitionDuration: 0,
    transitionState: "entered",
    contentVerticalAlignment: "center",
    size: "regular",
    titleWeight: TextWeight.SEMIBOLD,
};

export const ToastChildrenContent = (props: React.PropsWithChildren) => {
    const { children } = props;

    const theme = useTheme() as ITheme;

    if (typeof children === "string")
        return (
            <Text size={TextSize["X-SMALL"]} color={theme.grayscale[80]}>
                {children}
            </Text>
        );

    return <> {children} </>;
};

export const Toast = forwardRef<HTMLDivElement, ToastProps>((props, ref): JSX.Element => {
    const {
        children,
        appearance,
        icon: Icon,
        title,
        titleComponent,
        titleColor,
        contentVerticalAlignment,
        size,
        titleWeight,
        leftComponent,
        stretchContent,
        capitalizeTitle = true,
        hideCloseButton,
        ...attributes
    } = {
        ...defaultProps,
        ...props,
    };
    const { translations } = useTranslationsContext();
    const theme = useTheme();

    const getIconFromAppearance = (appearanceParam: ToastProps["appearance"]) => {
        if (appearanceParam === "success") return CheckLinear;
        if (appearanceParam === "error") return CloseLinear;
        if (appearanceParam === "info") return InfoCircleBold;
        return DangerLinear;
    };

    const getIconColorFromAppearance = (appearanceParam: ToastProps["appearance"]) => {
        if (appearanceParam === "info") return theme.colors.primary["100"];
        if (appearanceParam === "error") return theme.colors.error["100"];
        if (appearanceParam === "warning") return theme.colors.warning["100"];
        if (appearanceParam === "success") return theme.colors.success["100"];
        return theme.colors.primary["100"];
    };

    const getColorFromAppearance = (appearanceParam: ToastProps["appearance"]) => {
        if (appearanceParam === "info") return theme.colors.primary["10"];
        if (appearanceParam === "error") return theme.colors.error["10"];
        if (appearanceParam === "warning") return theme.colors.warning["10"];
        if (appearanceParam === "success") return theme.colors.success["10"];
        return theme.colors.primary["10"];
    };

    const getTitleFromAppearance = (appearanceParam: ToastProps["appearance"]) => {
        if (translations.toast) return translations.toast[appearanceParam];
        if (appearanceParam === "success") return "Success";
        if (appearanceParam === "error") return "Error";
        if (appearanceParam === "info") return "Info";
        return "Warning";
    };

    const color = getColorFromAppearance(appearance);

    const getLeftComponent = () => {
        if (leftComponent) return leftComponent;
        const IconComponent = Icon || getIconFromAppearance(appearance);

        return (
            <IconBox backgroundColor={getColorFromAppearance(appearance)} shape="round">
                <IconComponent color={getIconColorFromAppearance(appearance)} size={ICONSAX_SIZE.SMALL} />
            </IconBox>
        );
    };

    return (
        <Container
            color={color}
            transitionState={attributes.transitionState}
            transitionDuration={attributes.transitionDuration}
            ref={ref}
            contentVerticalAlignment={contentVerticalAlignment}
            size={size}
            stretchContent={stretchContent}
        >
            <Border color={color} height={contentVerticalAlignment === "stretch" ? "auto" : undefined} />
            {getLeftComponent()}

            <Body stretchContent={stretchContent}>
                {titleComponent ?? (
                    <Title color={titleColor || color} weight={titleWeight} capitalizeTitle={capitalizeTitle}>
                        <TooltipOnTextOverflow>{title || getTitleFromAppearance(appearance)}</TooltipOnTextOverflow>
                    </Title>
                )}

                <ToastChildrenContent>{children}</ToastChildrenContent>
            </Body>

            {attributes.autoDismiss === false && !hideCloseButton && (
                <CloseIcon size={ICONSAX_SIZE.SMALL} color={theme.grayscale[70]} onClick={() => attributes.onDismiss()} />
            )}
        </Container>
    );
});
